import { Button, OutlinedInput, Radio, Switch } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useState } from 'react';

import { ButtonV2, Col, Cols, Row, Rows, spacing, Text, TextButton } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IProps } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/common/types';
import { ISingleSelectOption, SingleSelect } from '~/src/dash/views/singleselect-view';

interface ISelectedMetricsProps {
  [key: number]: string[];
}

interface IHeaderRowProps {
  title: string;
  subheader: string;
  handleChange: () => void;
  isActive: boolean;
  isExpanded: boolean;
  handleRowExpansion: (type: string) => void;
  disabled: boolean;
}

interface IMetricConditionProps {
  index: number;
  metricsList: ISingleSelectOption[];
  condition: any;
  selectedMetrics: ISelectedMetricsProps;
  handleChangeMetricConditionParams: (name: string, value: string, index: number) => void;
  compareTypes: any;
  handleChangeMetricConditionValue: (
    elem: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => void;
  metricConditionEnabled: boolean;
  handleRemoveMetricCondition: (index: number) => void;
  handleUpdateSelectedMetrics: (metric: string, index: number) => void;
}

const TOTAL_TRACKED_ADS = 'TotalTrackedAds';
const METRIC_CONDITION = 'MetricConditions';
const CUSTOM = 'Custom';
const HIGHLIGHT = 'Highlight';
const FILTER_AND_HIGHLIGHT = 'FilterAndHighlight';
const BETWEEN = 'BT';

const HeaderRow = (headerProps: IHeaderRowProps) => {
  return (
    <Cols wrap responsive style={{ alignItems: 'center' }}>
      <Col style={{ minWidth: '300px' }}>
        <Text small bold>
          {headerProps.title}
        </Text>
      </Col>
      <Col>
        <Switch
          size='small'
          checked={headerProps.isActive}
          onChange={headerProps.handleChange}
          disabled={headerProps.disabled}
        />
      </Col>
      <Col smallHorizontalPadding style={{ minWidth: '100px' }}>
        <Text small>{headerProps.isActive ? 'Active' : 'Inactive'}</Text>
      </Col>
      <Col>
        <Cols style={{ alignItems: 'center' }}>
          <Col style={{ minWidth: '580px' }}>
            <Text small>{headerProps.subheader}</Text>
          </Col>
          <Col>
            <ButtonV2
              clearBackground
              style={{ height: '40px' }}
              onClick={() => headerProps.handleRowExpansion(TOTAL_TRACKED_ADS)}
            >
              <img
                src={`/assets/shared/svg/publisherPlatform/${
                  headerProps.isExpanded ? 'ArrowUp' : 'ArrowDown'
                }.svg`}
              />
            </ButtonV2>
          </Col>
        </Cols>
      </Col>
    </Cols>
  );
};

const MetricConditionRow = (IMetricConditionProps: IMetricConditionProps) => {
  const handleUpdateList = () => {
    const clonedList = cloneDeep(IMetricConditionProps.metricsList);
    if (
      IMetricConditionProps.index !== 0 &&
      Object.values(IMetricConditionProps.selectedMetrics).length > 0
    ) {
      const filteredList = clonedList.filter(
        (item) =>
          !IMetricConditionProps.selectedMetrics[IMetricConditionProps.index - 1].includes(
            item.value,
          ),
      );
      return filteredList;
    }
    return clonedList;
  };

  return (
    <Row noVerticalPadding smallHorizontalPadding>
      {IMetricConditionProps.index !== 0 && (
        <Text small style={{ padding: spacing.small }}>
          OR
        </Text>
      )}
      <Cols
        wrap
        responsive
        style={{
          alignItems: 'center',
          backgroundColor: 'rgb(248, 250, 255)',
        }}
      >
        <Col paddingBottom={spacing.medium} horizontalPadding>
          <SingleSelect
            name='metricName'
            label={''}
            items={handleUpdateList()}
            value={IMetricConditionProps.condition.metricName}
            onChange={(name: string, value: string) => {
              IMetricConditionProps.handleUpdateSelectedMetrics(value, IMetricConditionProps.index);
              IMetricConditionProps.handleChangeMetricConditionParams(
                name,
                value,
                IMetricConditionProps.index,
              );
            }}
            style={{
              width: '200px',
              border: `1px solid ${publicaColors.lightGray}`,
              borderRadius: '5px',
              background: 'white',
              fontSize: '14px',
              minWidth: '120px',
              paddingLeft: '2px',
            }}
          />
        </Col>
        <Col>
          <Text small> is</Text>
        </Col>
        <Col horizontalPadding paddingBottom={spacing.medium}>
          <SingleSelect
            name='type'
            label=''
            items={IMetricConditionProps.compareTypes}
            value={IMetricConditionProps.condition.type}
            onChange={(name: string, value: string) =>
              IMetricConditionProps.handleChangeMetricConditionParams(
                name,
                value,
                IMetricConditionProps.index,
              )
            }
            style={{
              width: '200px',
              border: `1px solid ${publicaColors.lightGray}`,
              borderRadius: '5px',
              background: 'white',
              fontSize: '14px',
              minWidth: '120px',
              paddingLeft: '2px',
            }}
          />
        </Col>
        <Col verticalPadding paddingLeft={spacing.medium}>
          <OutlinedInput
            name={IMetricConditionProps.condition.type === BETWEEN ? 'minValue' : 'value'}
            value={
              IMetricConditionProps.condition.type === BETWEEN
                ? IMetricConditionProps.condition.inputs.minValue
                : IMetricConditionProps.condition.inputs.value
            }
            placeholder=''
            type='number'
            onChange={(elem: React.ChangeEvent<HTMLInputElement>) =>
              IMetricConditionProps.handleChangeMetricConditionValue(
                elem,
                IMetricConditionProps.index,
              )
            }
            style={{
              width: '200px',
              height: '32px',
              background: 'white',
            }}
            disabled={!IMetricConditionProps.metricConditionEnabled}
          />
        </Col>
        {IMetricConditionProps.condition.type === BETWEEN && (
          <Col>
            <Cols style={{ alignItems: 'center' }}>
              <Col horizontalPadding>
                <Text small> and</Text>
              </Col>
              <Col>
                <OutlinedInput
                  name='maxValue'
                  value={IMetricConditionProps.condition.inputs.maxValue}
                  placeholder=''
                  type='number'
                  onChange={(elem: React.ChangeEvent<HTMLInputElement>) =>
                    IMetricConditionProps.handleChangeMetricConditionValue(
                      elem,
                      IMetricConditionProps.index,
                    )
                  }
                  style={{
                    width: '200px',
                    height: '32px',
                    background: 'white',
                  }}
                  disabled={!IMetricConditionProps.metricConditionEnabled}
                />
              </Col>
            </Cols>
          </Col>
        )}
        <Col paddingLeft='4px'>
          <Button
            onClick={() =>
              IMetricConditionProps.handleRemoveMetricCondition(IMetricConditionProps.index)
            }
            style={{ height: '34px' }}
          >
            <img src='/assets/shared/svg/publisherPlatform/Close.svg' />
          </Button>
        </Col>
      </Cols>
    </Row>
  );
};

export const CustomizeDataRow = (props: IProps) => {
  const [isExpandedTotalTrackedAds, setIsExpandedTotalTrackedAds] = useState<boolean>(false);
  const [isExpandedMetricConditions, setIsExpandedMetricConditions] = useState<boolean>(false);

  const [selectedMetrics, setSelectedMetrics] = useState<ISelectedMetricsProps>({});

  const handleUpdateSelectedMetrics = (metric: string, index: number) => {
    const clonedList = cloneDeep(selectedMetrics);
    if (index !== 0) {
      const prevList = clonedList[index - 1];
      clonedList[index] = [...prevList, metric];
      setSelectedMetrics(clonedList);
    } else {
      clonedList[index] = [metric];
      setSelectedMetrics(clonedList);
    }
  };

  const handleChangeRowExpansion = (type: string) => {
    if (type === TOTAL_TRACKED_ADS) {
      setIsExpandedTotalTrackedAds(!isExpandedTotalTrackedAds);
    } else {
      setIsExpandedMetricConditions(!isExpandedMetricConditions);
    }
  };

  return (
    <Rows
      responsive
      style={{
        width: 'fit-content',
        border: `1px solid ${publicaColors.lightGray}`,
        margin: '8px 32px 32px 0px',
      }}
    >
      {/* Minimum Total Tracked Ads row */}
      <Row horizontalPadding smallVerticalPadding>
        <HeaderRow
          title={'Minimum Total Tracked Ads Filter'}
          subheader={'Report will only include rows that exceed the following minimum total ads.'}
          handleChange={props.handleChangeTotalTrackedAdsEnabled}
          isActive={props.totalTrackedAdsEnabled}
          isExpanded={isExpandedTotalTrackedAds}
          handleRowExpansion={() => handleChangeRowExpansion(TOTAL_TRACKED_ADS)}
          disabled={!props.allSelectedMetrics.includes('TotalImps')}
        />
      </Row>
      {isExpandedTotalTrackedAds && (
        <Row style={{ padding: '0px 8px 32px 16px' }}>
          {props.totalTrackedAds === CUSTOM ? (
            <Cols
              style={{
                alignItems: 'center',
                marginLeft: spacing.small,
              }}
            >
              <Col width={300}></Col>
              <Col>
                <OutlinedInput
                  name='Custom Number'
                  value={props.customThreshold}
                  placeholder=''
                  type='number'
                  onChange={props.handleChangeCustomThreshold}
                  style={{
                    width: '200px',
                    height: '32px',
                    marginTop: spacing.medium,
                  }}
                />
              </Col>
              <Col paddingTop={spacing.medium} paddingLeft={spacing.small}>
                <Button onClick={props.handleCustomClose}>
                  <img src='/assets/shared/svg/publisherPlatform/Close.svg' />
                </Button>
              </Col>
            </Cols>
          ) : (
            <Cols
              wrap
              responsive
              style={{
                alignItems: 'center',
              }}
            >
              <Col width={300}></Col>
              <Col smallHorizontalPadding>
                <SingleSelect
                  name='Total Tracked Ads'
                  label=''
                  items={props.threshold}
                  value={props.totalTrackedAds}
                  onChange={props.handleChangeThresholdSelection}
                  style={{
                    width: '200px',
                    border: `1px solid ${publicaColors.lightGray}`,
                    borderRadius: '5px',
                    fontSize: '14px',
                    minWidth: '120px',
                    paddingLeft: '2px',
                  }}
                  disableAlphanumSort
                />
              </Col>
            </Cols>
          )}
        </Row>
      )}
      {/* Metric Condition Row */}
      <Row
        horizontalPadding
        smallVerticalPadding
        style={{
          borderTop: `1px solid ${publicaColors.lightGray}`,
        }}
      >
        <HeaderRow
          title={'Metric Conditions'}
          subheader={'Set up conditions to Filter and Highlight data in the report.'}
          handleChange={props.handleChangeMetricConditionsEnabled}
          isActive={props.metricConditionEnabled}
          isExpanded={isExpandedMetricConditions}
          handleRowExpansion={() => handleChangeRowExpansion(METRIC_CONDITION)}
          disabled={props.allSelectedMetrics.length === 0}
        />
      </Row>
      {isExpandedMetricConditions && (
        <Row>
          <Cols wrap responsive>
            <Col width={300}></Col>
            <Col>
              <Rows>
                <Row style={{ padding: '0px 8px 8px 8px' }}>
                  <Text small bold>
                    Set Up Conditions To
                  </Text>
                </Row>
                <Row noHorizontalPadding smallVerticalPadding>
                  <Cols wrap responsive style={{ alignItems: 'center' }}>
                    <Col>
                      <Cols>
                        <Col>
                          <Radio
                            size='small'
                            checked={props.setUpType === HIGHLIGHT}
                            onChange={() => props.handleChangeSetUpType(HIGHLIGHT)}
                            disabled={!props.metricConditionEnabled}
                          />
                        </Col>
                        <Col style={{ width: '280px' }}>
                          <Rows>
                            <Row style={{ padding: '6px 0 0 0' }}>
                              <Text small bold>
                                Highlight
                              </Text>
                            </Row>
                            <Row noPadding>
                              <Text extraSmall>
                                {' '}
                                Includes all data in the report and highlights cells that meet the
                                conditions
                              </Text>
                            </Row>
                          </Rows>
                        </Col>
                      </Cols>
                    </Col>
                    <Col>
                      <Cols>
                        <Col>
                          <Radio
                            size='small'
                            checked={props.setUpType === FILTER_AND_HIGHLIGHT}
                            onChange={() => props.handleChangeSetUpType(FILTER_AND_HIGHLIGHT)}
                            disabled={!props.metricConditionEnabled}
                          />
                        </Col>
                        <Col style={{ width: '280px' }}>
                          <Rows>
                            <Row style={{ padding: '6px 0 0 0' }}>
                              <Text small bold>
                                Filter and Highlight
                              </Text>
                            </Row>
                            <Row noPadding>
                              <Text extraSmall>
                                {' '}
                                Only Includes and highlights cells that meet the conditions
                              </Text>
                            </Row>
                          </Rows>
                        </Col>
                      </Cols>
                    </Col>
                  </Cols>
                </Row>
                <Row smallVerticalPadding smallHorizontalPadding>
                  <Text small bold>
                    Conditions
                  </Text>
                </Row>
                {props.metricConditions.map((condition, index) => (
                  <MetricConditionRow
                    index={index}
                    metricsList={props.metricsList}
                    condition={condition}
                    selectedMetrics={selectedMetrics}
                    handleChangeMetricConditionParams={props.handleChangeMetricConditionParams}
                    compareTypes={props.compareTypes}
                    handleChangeMetricConditionValue={props.handleChangeMetricConditionValue}
                    metricConditionEnabled={props.metricConditionEnabled}
                    handleRemoveMetricCondition={props.handleRemoveMetricCondition}
                    handleUpdateSelectedMetrics={handleUpdateSelectedMetrics}
                  />
                ))}
                <Row smallHorizontalPadding verticalPadding>
                  <TextButton
                    onClick={() => {
                      props.handleAddMetricCondition();
                    }}
                    disabled={!props.metricConditionEnabled}
                    size='small'
                  >
                    + New Condition
                  </TextButton>
                </Row>
              </Rows>
            </Col>
          </Cols>
        </Row>
      )}
    </Rows>
  );
};
